/*!
 * Cookies Plugin
 * Provides utilities for managing cookies.
 *
 * Originally ported from: https://bitbucket.org/inindca/predictive-engagement-sdk/src/master/src/cookies.js
 */

/*
  - Aug 16, 2024: This plugin just initializes cookies setup only for determining the TLD in localStorage plugin for cross domain storage support.
  */

import { isNil, DEPLOYMENT_PREFIXED_KEYS } from 'util';

const CANARY_COOKIE = 'genesysJsCanary';
const CANARY_COOKIE_VALUE = '(^_^)';

const UNIX_EPOCH_PLUS_ONE_SECOND = 'Thu, 01 Jan 1970 00:00:01 GMT';
const DEFAULT_PREFIX = '_';
const { deploymentId } = Genesys.c || {};

Genesys('registerPlugin', 'Cookies', (Cookies) => {
  const $$ = Cookies.data;
  let bCrossDomainStorage = false;

  /*
  * Configures all settings related to cookie management
  */
  function setupCookies(opts) {
    const options = opts || {};

    $$('prefix', typeof options.prefix === 'string' ? options.prefix : DEFAULT_PREFIX);

    let domain = options.domain || highestLevelDomain(document.location.hostname);

    // default cookie domain is the document location without the prefix www.
    if (domain.indexOf('www.') === 0) {
      domain = domain.slice(4);
    }

    $$('domain', domain);

    /**
     * Publish Cookies ready event only if its enabled, otherwise don't publish.
     * This is to avoid a race-condition where Identifiers are created twice because Journey Plugin is still subscribing to Cookies.ready event.
     * Keeping this way, will also allow switching between Cookies and LocalStorage plugin when needed.
     */
    //if (sStoragePlugin === 'Cookies') Cookies.ready();
  }

  /**
  * Returns the highest level domain possible to use as cookie domain, that is,
  * for subdomain.example.co.uk will return example.co.uk
  */
  function highestLevelDomain(domain) {
    const domainParts = domain.split('.');

    // the shortest domain should have at least a dot
    if (domainParts.length <= 2) {
      return domain;
    }

    let candidate;

    for (let i = domainParts.length - 2; i >= 0; i--) {
      candidate = domainParts.slice(i, domainParts.length).join('.');
      setCookie(CANARY_COOKIE, CANARY_COOKIE_VALUE, { domain: candidate, secure: true });

      if (getCookie(CANARY_COOKIE) === CANARY_COOKIE_VALUE) {
        break;
      }
    }

    removeCookie(CANARY_COOKIE, { domain: candidate });

    return candidate;
  }

  /*
  * Returns a domain value that can be used for setting cookies on.
  */
  function cookieDomain(domain) {
    return domain.split('.').length >= 2 ? `.${domain}` : domain;
  }

  /*
  * Provides a cookie value in the format expected by `document.cookies`.
  */
  function formatCookieValue(name, value, expires, domain, secure, path = '/') {
    const cookieParts = [];

    cookieParts.push(`${name}=${value || value === false ? value : ''}`);

    if (expires) {
      cookieParts.push(`expires=${expires}`);
    }
    if (domain) {
      cookieParts.push(`domain=${cookieDomain(domain)}`);
    }
    cookieParts.push(`path=${path}`);

    if (typeof secure === 'boolean')
      cookieParts.push(`secure=${secure}`); //WEBMESS-1681

    return cookieParts.join('; ');
  }

  /*
  * Retrieves a raw cookie value by name.
  */
  function getEncodedCookie(name) {
    const namePattern = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      const cookieValue = cookies[i].trim();

      if (cookieValue.indexOf(namePattern) === 0) {
        return cookieValue.substring(namePattern.length, cookieValue.length);
      }
    }

    return null;
  }

  /*
  * Returns the fully-qualified cookie name using the configured cookie prefix.
  */
  function cookieName(key) {
    const prefix = $$('prefix');

    if (key.indexOf(prefix) === 0) {
      return key;
    }

    const deploymentPrefix = DEPLOYMENT_PREFIXED_KEYS.includes(key) ? `${deploymentId}:` : '';

    return `${prefix}${deploymentPrefix}${key}`;
  }

  /*
  * Retrieves & decodes a cookie value by name.
  */
  function getCookie(key) {
    const name = cookieName(key);

    const encodedCookie = getEncodedCookie(name);
    const decodedCookie = !isNil(encodedCookie) ? decodeURIComponent(encodedCookie) : null;

    return decodedCookie;
  }

  /*
  * Sets a cookie value by name.
  */
  function setCookie(key, value, opts) {
    const options = opts || {};
    const name = cookieName(key);
    const domain = options.domain || $$('domain');
    const secure = opts.secure || '';

    let expireAt = null;

    // session cookies do not specify any expiration date
    if (options.expireInSecs) {
      const expireDate = new Date(Date.now() + options.expireInSecs * 1000);

      expireAt = expireDate.toGMTString();
    }

    // before setting the cookie, we remove this exact same cookie from any
    // other subdomain/domain that are accessible by the document location (URL)
    // this will avoid issues with cookies being set on different subdomains,
    // but being impossible to know under which domain when retrieving their values
    const domainParts = document.location.hostname.split('.');

    if (domainParts.length > 2) {
      removeCookie(name, {
        domain: domainParts.slice(0, domainParts.length).join('.'),
      });
    }

    document.cookie = formatCookieValue(name, value, expireAt, domain, secure);
  }

  /*
  * Deletes a cookie value by name.
  */
  function removeCookie(key, opts) {
    const options = Object.assign({ recursive: true }, opts || {});
    const name = cookieName(key);

    const domain = options.domain || $$('domain');

    if (options.recursive === true) {
      const domainParts = domain.split('.');
      let candidate;

      for (let i = domainParts.length - 2; i >= 1; i--) {
        candidate = domainParts.slice(i, domainParts.length).join('.');
        document.cookie = formatCookieValue(name, null, UNIX_EPOCH_PLUS_ONE_SECOND, candidate);
      }
    }

    document.cookie = formatCookieValue(name, null, UNIX_EPOCH_PLUS_ONE_SECOND, domain);
  }

  // WEBMESS-1707: Setup cookies only when cross-subdomain feature is enabled. Otherwise, this shouldn't be needed.
  // This will avoid unwanted console errors for normal localStorage customers when they try to run Messenger on invalid domains.
  Cookies.subscribe('GenesysJS.configurationReceived', (e) => {
    const { deploymentConfig } = e.data || {};
    const { messenger } = deploymentConfig || {};
    const { sessionPersistenceType } = messenger || {};

    if (typeof sessionPersistenceType == 'string' && sessionPersistenceType === 'AcrossSubdomains') bCrossDomainStorage = true;
    if (bCrossDomainStorage) setupCookies();
  });
});
